import { GEOLOCATION_PERMISSION_DENIED } from './constants';
/**
 * A few notes on browser detection:
 * iOS Browsers all use Safari at their core. Any web app built to be compatible with
 * Safari on iOS (at least as far as styling goes) should work on other iOS browsers.
 *
 * Desktop and iOS Safari are extremely similar. It seems like iOS Safari may have some
 * possible limitations compared to desktop, so develop for mobile first. If it works
 * there, it should work on desktop as well.
 */

export function safariDetect() {
  const ua = window.navigator.userAgent;
  const iOS = !!ua.match(/iPad/i) || !!ua.match(/iPhone/i);
  const webkit = !!ua.match(/WebKit/i);
  return iOS && webkit || !!window.ApplePaySession;
} // navigator.userAgent for IPAD does not show iPad nor Mobile for Version/13.1 (ipad 7th)
// By default Safari will now request the desktop websites on the iPad.
// for version below it will show ipad
// IPAD => Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_4) AppleWebKit/605.1.15 (KHTML, like Gecko) Version/13.1 Safari/605.1.15
// PHONE => "Mozilla/5.0 (iPhone; CPU iPhone OS 12_3_1 like Mac OS X) AppleWebKit/605.1.15 (KHTML, like Gecko) Version/12.1.1 Mobile/15E148 Safari/604.1"

export function mobileDetect() {
  return /Mobi|Android/i.test(navigator.userAgent);
}

function phoneDetect() {
  return /iPhone/i.test(navigator.userAgent);
}
/* 
  ##Device = iPad Pro
  ##Screen = 1024 to 1366px
*/


const breakpoints = {
  xl: 1700,
  lg: 1366,
  md: 845,
  sm: 768,
  xs: 480,
  xxs: 0
}; // isSmallTouchDevice => ipad or iphone
// needed as no reliable way with navigator.userAgent

export function isSmallTouchDevice() {
  // For ios below 13 this is how to see if ipad
  const isMobile = mobileDetect(); // For ios above 13 Use Navigator.maxTouchPoints to detect if the user's device has a touchscreen.

  const hasTouchPoint = navigator.maxTouchPoints > 1; // but Desktop have touchscreen as well...
  // IPad width is mainly 1024, means as well desktop in xs mode is detected ipad

  const isLargeScreen = window.innerWidth > breakpoints.lg;
  return isMobile || hasTouchPoint && !isLargeScreen;
} // iPhone 6/7/8 is 667px
// iPhone 12 Pro is 844px

/**
 * “Phone” for **runtime / product rules** (e.g. caps, logger context, widget open limits).
 *
 * This is **not** the same as responsive **layout** breakpoints. For stacking columns, grid
 * changes, and “narrow vs wide” CSS, the app often uses **viewport width** (commonly
 * `(max-width: 600px)` to align with MUI default **`sm` / `down('sm')`**, or similar) so
 * desktop and tablet with viewport **wider than 600px** keep the wide layout regardless
 * of user agent. See
 * `.cursor/rules/ctm-phone-viewport.mdc` in this repo.
 *
 * `isPhoneDevice` combines UA (e.g. iPhone, generic Mobi) with
 * `window.innerWidth < breakpoints.md` (845) and touch in the last branch.
 */

export function isPhoneDevice() {
  const isiPhone = phoneDetect(); // Samsung

  const isMobile = mobileDetect();
  const hasTouchPoint = navigator.maxTouchPoints > 1;
  const isPhoneScreen = window.innerWidth < breakpoints.md;
  return isiPhone || isMobile || hasTouchPoint && isPhoneScreen;
}
/**
 * GEOLOCATION
 */

/**
 * Wrapper function for promisifying the geolocation api. This api is async, however its
 * ancient and is only usable via callbacks.  This wrappers allows us to await the geolocation
 * call.
 * This function requires HTTPS or it will fail.  For local development, you can use
 *  chrome://flags/#unsafely-treat-insecure-origin-as-secure
 * allows you to list specific urls to be treated as secure.
 * Listing http://reactdev.mumms.com allows the geolocation to work.
 *
 * NOTE: This function resolves to a Position type object due to CTM using an older
 * version of TypeScript (3.9.10).  After Typescript Version 4.1.0, Position was changed to
 * GeolocationPosition and Position is no longer valid.
 *
 * @returns  Promise<Position>
 */

export async function getUsersGeolocation() {
  return new Promise((resolve, reject) => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(position => {
        if (position) resolve(position);
      }, error => {
        if (error) {
          reject(error);
        }
      }, {
        enableHighAccuracy: true,
        maximumAge: 0 // keep 0, so that we always grab new coordinants.

      });
    } else {
      reject(new Error('navigator.geolocation is not available'));
    }
  });
}
/**
 * Checks the error thrown by Geolocation API.  In the cases where the error is not from
 * users blocking the request, log the error.
 * @param error
 */

export function handleGeolocationError(error) {
  if (error && error.code && error.code !== GEOLOCATION_PERMISSION_DENIED) {
    console.error(error);
  }
}